import styled from "styled-components";
import React, { useState, useRef, useEffect } from "react";
import { red } from "../../styles";
import AwardCard from "./AwardCard";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const Page = styled.main`
  text-align: left;
  color: #3c3b3b;
`;

const SectionContent = styled.div`
  h2 {
    margin-top: 0;
  }
`;

const Panel = styled.div`
  text-align: left;
`;

const Loading = styled.div`
  margin: 30px auto;
  font-size: 40px;
  text-align: center;
  color: ${red};
`;

function EndowmentArchive() {
  const years = ["2025", "2024"];
  const [awardeeListsByYear, setAwardeeListsByYear] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const topRef = useRef(null);
  const recipients2024Ref = useRef(null);

  const scrollTo2024Recipients = (event) => {
    event.preventDefault();

    if (recipients2024Ref.current) {
      recipients2024Ref.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });

      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${window.location.search}#endowment-2024-recipients`
      );
    }
  };

  const groupAwardeesByFund = (data) => {
    const sortedList = data.reduce((acc, awardee) => {
      if (acc[awardee.endowment_fund]) {
        acc[awardee.endowment_fund].awardeeList.push(awardee);
      } else {
        acc[awardee.endowment_fund] = {
          name: awardee.endowment_fund,
          awardeeList: [awardee],
        };
      }

      return acc;
    }, {});

    for (let awardId in sortedList) {
      sortedList[awardId].awardeeList.sort((a, b) => {
        return (a.last_name || "").localeCompare(b.last_name || "");
      });
    }

    return sortedList;
  };

  useEffect(() => {
    Promise.all(
      years.map((year) =>
        fetch(`/api/getMasterReport?year=${year}`)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Error fetching ${year} data`);
            }
            return response.json();
          })
          .then((data) => {
            return [year, groupAwardeesByFund(data)];
          })
      )
    )
      .then((results) => {
        setAwardeeListsByYear(Object.fromEntries(results));
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setError("Error fetching data");
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <Page>
        <Loading>
          <FontAwesomeIcon icon={faSpinner} className="fa-spin" />
        </Loading>
      </Page>
    );
  } else if (error) {
    return (
      <span className="row">
        <span className="alert alert-danger" role="alert">
          {error}
        </span>
        <br />
      </span>
    );
  }

  return (
    <SectionContent>
      <div ref={topRef} />

      <h1
        style={{
          textAlign: "left",
          color: "rgb(0, 55, 103)",
          fontSize: "32px",
          fontWeight: "bold",
        }}
      >
        Endowment Awardees
      </h1>

      <p style={{ textAlign: "left" }}>
        The SOT Endowment Fund is a family of funds that support SOT programs
        and members. Many of the funds sponsor awards designed to encourage,
        assist, and highlight toxicology research and toxicologists.
      </p>

      <p style={{ textAlign: "left" }}>
        <a
          href="https://www.toxicology.org/endowment/Eawards/recipientsFull.asp"
          target="_blank"
          rel="noopener noreferrer"
        >
          Endowment Award Recipients 2023 and Earlier
        </a>
      </p>
      <p style={{ textAlign: "left" }}>
        <a href="#endowment-2024-recipients" onClick={scrollTo2024Recipients}>
          Endowment 2024 Recipients
        </a>
      </p>

      {years.map((year) => {
        const awardeeList = awardeeListsByYear[year] || {};

        return (
          <React.Fragment key={year}>
            <h1
              ref={year === "2024" ? recipients2024Ref : null}
              id={year === "2024" ? "endowment-2024-recipients" : undefined}
              style={{
                textAlign: "left",
                color: "rgb(0, 55, 103)",
                fontSize: "32px",
                fontWeight: "bold",
                marginTop: "20px",
              }}
            >
              {year}
            </h1>

            <Panel>
              <div className="row">
                {Object.keys(awardeeList).length > 0 &&
                  Object.keys(awardeeList)
                    .sort()
                    .map((awardId, index) => (
                      <AwardCard
                        key={`fund-${year}-${index}`}
                        year={year}
                        awardName={awardeeList[awardId].name}
                        awardeeList={awardeeList[awardId].awardeeList}
                      />
                    ))}
              </div>
            </Panel>
          </React.Fragment>
        );
      })}
    </SectionContent>
  );
}

export default EndowmentArchive;